import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MoreHorizontal, Eye, ChefHat, CheckCircle, XCircle, Printer, Smartphone, Truck } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { OrderStatusBadge } from "./OrderStatusBadge";
import { OrderDetailsDialog } from "./OrderDetailsDialog";
import { printCustomerReceipt } from "@/lib/order-printing";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import type { OrderWithDetails } from "@/hooks/useOrdersList";
import type { Database } from "@/integrations/supabase/types";

type OrderStatus = Database["public"]["Enums"]["order_status"];

const orderTypeLabels: Record<string, string> = {
  table: "Mesa",
  tab: "Comanda",
  counter: "Balcão",
  delivery: "Delivery",
};

interface OrdersTableProps {
  orders: OrderWithDetails[];
  onUpdateStatus: (params: { orderId: string; newStatus: OrderStatus }) => void;
  onCancelOrder: (orderId: string) => void;
  isUpdating: boolean;
}

export function OrdersTable({
  orders,
  onUpdateStatus,
  onCancelOrder,
  isUpdating,
}: OrdersTableProps) {
  const [selectedOrder, setSelectedOrder] = useState<OrderWithDetails | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState<string | null>(null);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const handleViewDetails = (order: OrderWithDetails) => {
    setSelectedOrder(order);
    setDetailsOpen(true);
  };

  const handleCancelClick = (orderId: string) => {
    setOrderToCancel(orderId);
    setCancelDialogOpen(true);
  };

  const confirmCancel = () => {
    if (orderToCancel) {
      onCancelOrder(orderToCancel);
    }
    setCancelDialogOpen(false);
    setOrderToCancel(null);
  };

  const getNextStatus = (order: OrderWithDetails): OrderStatus | null => {
    const currentStatus = order.status;
    const isDelivery = !!order.delivery_address;
    
    // For delivery orders, after ready -> out_for_delivery -> delivered
    // For non-delivery orders, after ready -> delivered
    const flow: Record<OrderStatus, OrderStatus | null> = {
      pending: "preparing",
      preparing: "ready",
      ready: isDelivery ? "out_for_delivery" : "delivered",
      out_for_delivery: "delivered",
      delivered: null,
      cancelled: null,
      paid: null,
    };
    return flow[currentStatus];
  };

  const handlePrintReceipt = async (order: OrderWithDetails) => {
    try {
      await printCustomerReceipt({
        orderNumber: order.order_number,
        items: order.items.map((item) => ({
          name: item.product_name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total_price: item.total_price,
        })),
        subtotal: Number(order.subtotal),
        discount: Number(order.discount) || 0,
        total: Number(order.total),
        paymentMethod: order.payments[0]?.payment_method?.name || "Não informado",
        customerName: order.customer_name || undefined,
      });
    } catch (error) {
      console.error("Error printing receipt:", error);
      toast.error("Erro ao imprimir via do cliente");
    }
  };

  if (orders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
          <ChefHat className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium">Nenhum pedido encontrado</h3>
        <p className="text-sm text-muted-foreground">
          Ajuste os filtros ou aguarde novos pedidos
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[140px]">Pedido</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead>Data/Hora</TableHead>
              <TableHead>Itens</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[70px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.map((order) => {
              const isPublicOrder = (order as OrderWithDetails & { is_public_order?: boolean }).is_public_order;
              return (
                <TableRow 
                  key={order.id}
                  className={isPublicOrder ? "bg-orange-50/50 dark:bg-orange-900/10 border-l-4 border-l-orange-500" : ""}
                >
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      #{order.order_number}
                      {isPublicOrder && (
                        <Badge variant="outline" className="text-xs gap-1 border-orange-300 text-orange-600 dark:text-orange-400">
                          <Smartphone className="h-3 w-3" />
                          Digital
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {order.delivery_address ? (
                        <>
                          <Truck className="h-3.5 w-3.5 text-blue-600" />
                          <span className="text-blue-600 font-medium">Delivery</span>
                        </>
                      ) : (
                        <>
                          {orderTypeLabels[order.order_type]}
                          {order.table_number && ` ${order.table_number}`}
                          {order.tab_number && ` ${order.tab_number}`}
                        </>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {order.customer_name || (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {format(new Date(order.created_at), "dd/MM HH:mm", {
                      locale: ptBR,
                    })}
                  </TableCell>
                  <TableCell>{order.items.length} item(s)</TableCell>
                  <TableCell>{formatCurrency(Number(order.total))}</TableCell>
                  <TableCell>
                    <OrderStatusBadge status={order.status} />
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" disabled={isUpdating}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleViewDetails(order)}>
                          <Eye className="h-4 w-4 mr-2" />
                          Ver Detalhes
                        </DropdownMenuItem>

                        <DropdownMenuItem onClick={() => handlePrintReceipt(order)}>
                          <Printer className="h-4 w-4 mr-2" />
                          Reimprimir Via
                        </DropdownMenuItem>
                        
                        {getNextStatus(order) && (
                          <DropdownMenuItem
                            onClick={() =>
                              onUpdateStatus({
                                orderId: order.id,
                                newStatus: getNextStatus(order)!,
                              })
                            }
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            {order.status === "ready" && order.delivery_address 
                              ? "Saiu para Entrega" 
                              : "Avançar Status"}
                          </DropdownMenuItem>
                        )}

                        {order.status !== "cancelled" && order.status !== "delivered" && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => handleCancelClick(order.id)}
                            >
                              <XCircle className="h-4 w-4 mr-2" />
                              Cancelar Pedido
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <OrderDetailsDialog
        order={selectedOrder}
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
      />

      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar Pedido</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja cancelar este pedido? Esta ação não pode ser
              desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmCancel}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Cancelar Pedido
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
